import { jwtDecode, JwtPayload } from "jwt-decode";
import moment from "moment";
import {
  Address,
  CorporateType,
  CorporateTypeTranslated,
  DocumentBusinessType,
  ForeignTaxDetailWithAddress,
  Gender,
  GenderInitials,
  MidgardQuestions,
  PersonType,
  PitbullCountry,
  PitbullProvinces,
  StepEndpoint,
} from "../types";
import heic2any from "heic2any";
import { showToastMessage } from "@almafintech/react-components";
import {
  checkIsEmailAvailable,
  customToastifyError,
  parseStateAddress,
  getCompanyData,
} from "../utils";
import { getIndividualQuestions, getProvinces, sendStep, validateRenaper } from "../connectors/connectors";
import Fuse from "fuse.js";
import {
  CalendarDate,
  parseDate as parseStringToCalendarDate,
} from "@internationalized/date";
import * as Sentry from "@sentry/react";
import {
  addSendedStep,
  onUpdateCompletedStep,
  onUpdateFormData,
  onUpdateReachedStep,
  onUpdateStep,
  onUpdateSubStep,
} from "../reducers/onboardingDataReducer";
import { updateRenaperData } from "../reducers/renaperReducer";
import { addOneValidation } from "../reducers/validationsReducer";
import { FormikErrors, FormikValues } from "formik";
import { AppDispatch, RootState } from "./store";

interface OurJwtPayload extends JwtPayload {
  uuid: string;
}
export const getDraftUUIDFromToken = ({ token }: { token: string }) => {
  const decoded: OurJwtPayload = jwtDecode(token);
  const uid = decoded.uuid;

  return uid;
};

//The bearer token is valid for 48 hours
export const isTokenActive = ({ token }: { token: string }) => {
  const decoded: OurJwtPayload = jwtDecode(token);
  const exp = decoded.exp;

  return exp ? moment.unix(exp).isAfter(moment()) : false;
};

//This removes the step with the same type. Prevents duplicates.
export const getDraftStepsToSend = (
  steps: StepEndpoint[],
  newStep: StepEndpoint
) => {
  const otherSteps = steps.filter(
    (step: StepEndpoint) => step["@type"] !== newStep["@type"]
  );

  return [...otherSteps, newStep];
};

export const handleSendStep = async ({
  dispatch,
  personType,
  sendedSteps,
  stepId,
  step,
  email,
}: {
  personType: PersonType;
  sendedSteps: StepEndpoint[];
  stepId: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  step: StepEndpoint | any;
  email: string;
  dispatch: AppDispatch;
}) => {
  const body = {
    companyType: getCompanyData().companyName,
    personType: personType,
    email: email,
    steps: getDraftStepsToSend(sendedSteps, step),
  };
  await sendStep({ stepId, body });
  dispatch && dispatch(addSendedStep(step));
};

export const parseDate = (date: string) =>
  moment(date, "DD/MM/YYYY").format("YYYY-MM-DD");

export const parseBirthDate = (date: string, splitCharacter?: string) => {
  const [year, month, day] = date.split(splitCharacter ?? "-");
  return `${day}/${month}/${year}`;
};

export const parseGenderFromInitial: Record<GenderInitials, Gender> = {
  M: "MALE",
  F: "FEMALE",
};

export const parseGenderToInitial = (gender: string) => {
  if (gender === "MALE") return "M";
  if (gender === "FEMALE") return "F";
  return "X";
};

export const parseTelephone = (telephone: string): string => {
  // Validate that what we receive is '54' or '+54' followed by 10 more digits
  const regex = /^\+?54\d{10}$/;

  if (regex.test(telephone)) {
    // Remove the '+' if present and return the complete number
    return telephone.replace(/^\+/, "");
  } else {
    // Return an empty string if the format is not valid
    return "";
  }
};

export const capitalizeString = (name: string) => {
  if (!name) return "";
  const capitalize = (str: string) =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  return name
    .split(" ")
    .map((substr) => capitalize(substr))
    .join(" ");
};

export const formatDNI = (dni: string): string => {
  if (!dni) return "";
  const cleanDni = dni.replace(/\D/g, ""); // Remove non-digits
  return cleanDni.length === 7 ? `0${cleanDni}` : cleanDni;
};

export const resetFields = (
  fieldsToReset: string[],
  setFieldValue: (
    field: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
    shouldValidate?: boolean
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Promise<FormikErrors<FormikValues>> | Promise<any>
) => fieldsToReset.forEach((field) => setFieldValue(field, ""));

export const completeAddress = (
  addressFields: Record<string, string>,
  address: Address,
  setFieldValue: (
    field: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
    shouldValidate?: boolean
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Promise<FormikErrors<FormikValues>> | Promise<any>,
  getProvincesFilteredByCountry: (country?: string) => PitbullProvinces
) => {
  const { zipCode, state, city, municipality, street, number, country } =
    address;

  const provinces = getProvincesFilteredByCountry(country);
  const fusedProvince = fuseProvince(provinces, state, city);
  // If no records are found in FUSE, we set the first province by default to avoid breaking
  const finalProvince = fusedProvince || provinces[0].name;

  setFieldValue(addressFields.zipCode, zipCode);
  setFieldValue(addressFields.state, finalProvince);
  setFieldValue(addressFields.city, city);
  setFieldValue(addressFields.municipality, municipality);
  setFieldValue(addressFields.street, street);
  setFieldValue(addressFields.number, number);

  const newFullAddress = `${street} ${number} ${city ? city : ""} ${
    municipality ? municipality : ""
  }`;
  setFieldValue(addressFields.fullAddress, newFullAddress);
};

export const blobToFile = (blob: Blob, name: string, type: string) => {
  return new File([blob], name, {
    type: type,
  });
};

const heicFileTypes = ["image/heif", "image/heic", ".heic", ".heif"];

export const heicToJpeg = async (file: File): Promise<File> => {
  // HEIC TO JPEG CONVERSION
  if (heicFileTypes.includes(file.type)) {
    const newType = "image/jpeg";

    const heicBlob = await heic2any({ blob: file, toType: newType });

    if (Array.isArray(heicBlob)) return file;

    const newFileName = file.name.replace(/\.(heic|heif)$/i, ".jpg");

    const fileFromBlob = blobToFile(heicBlob, newFileName, newType);

    return fileFromBlob;
  }

  return file;
};

export const translateCorporate: Record<
  CorporateTypeTranslated,
  CorporateType
> = {
  saCorporation: "SA - Sociedad Anónima",
  llcLimitedLiabilityCompany: "SRL - Sociedad de Responsabilidad Limitada",
  associationFoundationCooperativeChamber:
    "Asociación / Cámara / Cooperativa / Fundación / Mutual",
  trust: "Fideicomiso",
  simplifiedJointStockCompany: "SAS - Sociedad por Acciones Simplificada",
  reciprocalGuaranteeCompany: "SGR - Sociedad de Garantía Recíproca",
  consortium: "Consorcio",
  governmentAgencyOrEntity: "Organismo y/o Entidad Gubernamental",
  other: "Otro",
};

export const translateCorporateReversed = Object.fromEntries(
  Object.entries(translateCorporate).map(([key, value]) => [value, key])
) as Record<CorporateType, CorporateTypeTranslated>;

export const businessDocuments: Record<string, DocumentBusinessType> = {
  balance: "BALANCE",
  worthStatement: "WORTH_STATEMENT",
  sharesRegister: "SHARES_REGISTER",
  other: "OTHERS",
  statute: "STATUTE",
  poa: "POA",
  authorities: "AUTHORITIES",
  membersNote: "MEMBERS_NOTE",
  shareholdersNote: "SHAREHOLDERS_NOTE",
  trustNote: "TRUST_NOTE",
  partnersNote: "PARTNERS_NOTE",
  partnersList: "PARTNERS_LIST",
  uifInscriptionCertificate: "UIF_INSCRIPTION_CERTIFICATE",
  expenseLiquidation: "EXPENSE_LIQUIDATION",
  administratorDesignationSas: "AUTHORITIES",
  administratorDesignationConsortium: "AUTHORITIES",
  horizontalPropertyRegulationsScanned: "HORIZONTAL_PROPERTY_REGULATIONS",
  trustConstitutionContract: "TRUST_CONSTITUTION_CONTRACT",
  trusteeDocumentationStatute: "STATUTE",
  trusteeDocumentationAuthorities: "AUTHORITIES",
  coownersList: "CO_OWNER_LIST",
};

export const mapDocumentBusinessType = (type: string) =>
  businessDocuments[type];

export const mapDocumentBusinessTypeFromValue = (type: DocumentBusinessType) =>
  Object.keys(businessDocuments).find(
    (key) => businessDocuments[key] === type
  ) as keyof typeof businessDocuments;

export const getBusinessDocumentsByCorporateType = (
  corporateType: CorporateTypeTranslated
): DocumentBusinessType[] => {
  switch (corporateType) {
    case "saCorporation":
      return [
        "STATUTE",
        "POA",
        "BALANCE",
        "SHARES_REGISTER",
        "AUTHORITIES",
        "SHAREHOLDERS_NOTE",
        "OTHERS",
      ];
    case "llcLimitedLiabilityCompany":
      return ["STATUTE", "POA", "BALANCE", "SHAREHOLDERS_NOTE", "OTHERS"];
    case "associationFoundationCooperativeChamber":
      return [
        "STATUTE",
        "POA",
        "BALANCE",
        "AUTHORITIES",
        "MEMBERS_NOTE",
        "OTHERS",
      ];
    case "consortium":
      return [
        "HORIZONTAL_PROPERTY_REGULATIONS",
        "POA",
        "AUTHORITIES",
        "EXPENSE_LIQUIDATION",
        "OTHERS",
      ];
    case "trust":
      return [
        "TRUST_CONSTITUTION_CONTRACT",
        "POA",
        "BALANCE",
        "UIF_INSCRIPTION_CERTIFICATE",
        "TRUST_NOTE",
        "AUTHORITIES",
        "STATUTE",
        "OTHERS",
      ];
    case "governmentAgencyOrEntity":
      return ["STATUTE", "POA", "BALANCE", "AUTHORITIES", "OTHERS"];
    case "other":
      return [
        "STATUTE",
        "AUTHORITIES",
        "WORTH_STATEMENT",
        "SHAREHOLDERS_NOTE",
        "OTHERS",
      ];
    case "reciprocalGuaranteeCompany":
      return [
        "STATUTE",
        "POA",
        "BALANCE",
        "AUTHORITIES",
        "PARTNERS_NOTE",
        "PARTNERS_LIST",
        "OTHERS",
      ];
    case "simplifiedJointStockCompany":
      return [
        "STATUTE",
        "POA",
        "BALANCE",
        "AUTHORITIES",
        "SHAREHOLDERS_NOTE",
        "OTHERS",
      ];
    default:
      return [];
  }
};

export const getDocumentTypeParsed = (
  corporateType: CorporateTypeTranslated,
  documentType: DocumentBusinessType
) => {
  switch (documentType) {
    case "BALANCE":
      return "Balance certificado por CPCE o doc. patrimonial";
    case "WORTH_STATEMENT":
      return "Estado de situación patrimonial certificado por CPCE";
    case "SHARES_REGISTER":
      return "Registro de accionistas";
    case "OTHERS":
      return "Otro";
    case "STATUTE":
      if (corporateType === "consortium")
        return "Reglamento de propiedad horizontal escaneado";
      if (corporateType === "trust")
        return "Contrato de constitución del Fideicomiso";
      return "Estatuto o contrato social escaneado";
    case "POA":
      return "Poder de los firmantes";
    case "AUTHORITIES":
      if (
        corporateType === "consortium" ||
        corporateType === "simplifiedJointStockCompany"
      )
        return "Acta de designación de administrador";
      return "Acta de designación de autoridades";
    case "MEMBERS_NOTE":
      return "Nota de miembros";
    case "SHAREHOLDERS_NOTE":
      return "Nota de accionistas y beneficiarios finales";
    case "TRUST_NOTE":
      return "Nota de fiduciantes, fiduciarios y beneficiarios";
    case "PARTNERS_NOTE":
      return "Nota de socios protectores y beneficiarios";
    case "PARTNERS_LIST":
      return "Nómina de socios";
    case "UIF_INSCRIPTION_CERTIFICATE":
      return "Constancia de inscripción ante UIF";
    case "EXPENSE_LIQUIDATION":
      return "Liquidación de expensas";
    case "HORIZONTAL_PROPERTY_REGULATIONS":
      return "Reglamento de propiedad horizontal escaneado";
    case "TRUST_CONSTITUTION_CONTRACT":
      return "Contrato de constitución del Fideicomiso";
    default:
      return "";
  }
};

export const replaceSpecialCharacters = (text: string) => {
  return text
    .normalize("NFD") // Decompose accented characters
    .replace(/[\u0300-\u036f]/g, "") // Remove accent marks
    .replace(/[()]/g, ""); // Remove parentheses
};

export const parseRegistrationAuthority = (authority: string) => {
  switch (authority) {
    case "OTHER":
      return "Otro";
    case "DIRECTORATE_OF_LEGAL_ENTITIES":
      return "Dirección de Personas Jurídicas";
    case "GENERAL_INSPECTION_OF_JUSTICE":
      return "Inspección General de Justicia";
    default:
      return "";
  }
};

export const getForeignTaxDetails = (
  foreignTaxDetailBool: string,
  foreignTaxDetail?: {
    idNumber: string;
  } & ForeignTaxDetailWithAddress["address"]
) =>
  foreignTaxDetailBool === "SI" && foreignTaxDetail
    ? {
        foreignTaxDetail: {
          idNumber: String(foreignTaxDetail.idNumber).trim(),
          address: {
            street: foreignTaxDetail.street,
            number: foreignTaxDetail.number,
            zipCode: foreignTaxDetail.zipCode,
            city: foreignTaxDetail.city,
            state: foreignTaxDetail.state,
            country: foreignTaxDetail.country,
            floor: foreignTaxDetail.floor,
            apartment: foreignTaxDetail.apartment,
            municipality: foreignTaxDetail.municipality,
          },
        },
      }
    : {};

export const showCustomToastifyError = (error: string) => {
  let errorDescription = "";

  if (error === "CXU_NOT_OWNER")
    errorDescription = "La cuenta ingresada no está asociada a tu CUIT/CUIL.";
  if (error === "CXU_NOT_FOUND")
    errorDescription =
      "CBU/CVU no existente, por favor revisá los datos ingresados.";
  if (error === "DNI_NO_VIGENTE")
    errorDescription =
      "El DNI ingresado no es vigente. Por favor revisá los datos ingresados.";
  if (error === "INVALID_DATA_DNI")
    errorDescription =
      "Los datos ingresados no coinciden con los registros oficiales. Por favor, verificá que estén correctos";
  return showToastMessage(customToastifyError(errorDescription), {
    containerId: "formAlert",
    type: "error",
  });
};

export const getQuestions = async (): Promise<MidgardQuestions> => {
  try {
    const { data } = await getIndividualQuestions();
    return data;
  } catch (error) {
    throw new Error("Error al obtener las preguntas");
  }
};

export const getProvincesByIso = async (
  iso?: string
): Promise<PitbullProvinces> => {
  try {
    const { data } = await getProvinces(iso);
    return data;
  } catch (error) {
    throw new Error("Error al obtener las preguntas");
  }
};

export const fuseProvince = (
  provinces: PitbullProvinces,
  state: string,
  city: string
): string => {
  const fuse = new Fuse(provinces, {
    useExtendedSearch: true,
    keys: ["name"],
    threshold: 0.3, // This is the score, beyond 0.3 it is not very accurate
  });
  const parsedState = parseStateAddress(state);

  // Search by administrative_area_level_1
  let fuseLogs = fuse.search(parsedState);

  if (fuseLogs.length === 0) {
    // Search by administrative_area_level_2
    fuseLogs = fuse.search(city);
  }

  // If no records are found in FUSE, we set the first province by default to avoid breaking
  return fuseLogs[0]?.item.name || provinces[0].name;
};

export const stringToCalendarDate = (
  dateString: string
): CalendarDate | null => {
  try {
    if (!dateString) return null;

    let [year, month, day]: Array<string | number> = dateString
      .split("-")
      .map(Number);

    year = year.toString().padStart(4, "0");
    month = month.toString().padStart(2, "0");
    day = day.toString().padStart(2, "0");

    const calendarDate = parseStringToCalendarDate(`${year}-${month}-${day}`);

    return calendarDate;
  } catch (error) {
    console.error("Error parsing date", error);
    return null;
  }
};

// Convert CalendarDate to string format YYYY-MM-DDDD
export const calendarDateToString = (date: CalendarDate): string => {
  const day = date.day.toString().padStart(2, "0");
  const month = date.month.toString().padStart(2, "0");
  const year = date.year;
  return `${year}-${month}-${day}`;
};

/**
 * Converts a File or Blob to a Base64 string
 * @param {File | Blob} file - The file to convert
 * @returns {Promise<string>} Promise resolving to the Base64 string
 */
export const fileToBase64 = (file: File | Blob): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject(new Error("FileReader did not return a string"));
      }
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Converts a Base64 string back to a File object
 * @param {string} base64String - The Base64 string to convert
 * @param {string} filename - The name to give the resulting file
 * @param {string} [mimeType] - The MIME type (defaults to image/png if not provided)
 * @returns {File} The resulting File object
 */
export const base64ToFile = (
  base64String: string,
  filename: string,
  mimeType: string = "image/png"
): File => {
  // Extract the Base64 data part if it's a data URL
  const base64Data = base64String.includes("base64,")
    ? base64String.split("base64,")[1]
    : base64String;

  // Decode the Base64 data
  const byteString = atob(base64Data);

  // Create an array buffer
  const arrayBuffer = new ArrayBuffer(byteString.length);
  const uint8Array = new Uint8Array(arrayBuffer);

  // Fill the array with the decoded bytes
  for (let i = 0; i < byteString.length; i++) {
    uint8Array[i] = byteString.charCodeAt(i);
  }

  // Create a Blob with the array buffer
  const blob = new Blob([arrayBuffer], { type: mimeType });

  // Create a File from the Blob
  return new File([blob], filename, { type: mimeType });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const handleCatchRenaper = (error: any) => {
  if (error && typeof error === "object" && "response" in error) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Sentry.captureException((error as any).response);
  } else {
    Sentry.captureException(error);
  }
  if (error instanceof Error) {
    // Type guard for 'code' property
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((error as any).code === "ERR_BAD_RESPONSE") {
      throw new Error("INVALID_DATA_DNI");
    }
    throw new Error(error.message);
  } else {
    throw new Error(String(error));
  }
};

export const onContinueRenaper = async (
  values: FormikValues,
  dispatch: AppDispatch,
  state: RootState,
  countries: PitbullCountry[],
  addressKey: "addresses" | "representativesAddresses" = "addresses"
) => {
  try {
    const { gender, tramitNumber, identityDocument } = values;
    const { data } = await validateRenaper({
      gender,
      order: tramitNumber,
      number: identityDocument,
    });

    const failedStatus = ["FAILED", "CHECK"];

    // TYPES OF STATUS "OK" | "FAILED" | "CHECK"
    if (!data || !data.status || failedStatus.includes(data.status)) {
      Sentry.captureException(new Error(), {
        extra: { status: data?.status, data: data.validations[0] },
      });

      // ERROR NO_VIGENTE
      if (data.status === "CHECK") {
        const statusDetail = JSON.parse(data.validations[0]?.statusDetail);
        if (
          statusDetail.code === "10001" &&
          statusDetail.message === "Exito" &&
          statusDetail.valid === "No vigente"
        ) {
          throw new Error("DNI_NO_VIGENTE");
        }
      }
      throw new Error("FAILED at validate Renaper");
    } else if (data.status === "OK" && dispatch) {
      const information = data.validations[0].information;

      // Convert string to JSON
      const informationParsed = JSON.parse(information);
      dispatch(
        updateRenaperData({
          ...informationParsed,
          externalCode: data.validations[0].externalCode,
        })
      );
      dispatch(
        addOneValidation({
          name: "RENAPER",
          externalCode: data.validations[0].externalCode,
        })
      );
      const { isExternalInformation } = state.onboardingData;
      const {
        names,
        lastNames,
        birthdate,
        countryBirth,
        streetAddress,
        numberStreet,
        country,
        city,
        nationality,
        municipality,
      } = informationParsed;
      const getParsedCountry = (countryToFind: string | undefined) => {
        return countries.find(({ name }) => {
          const lowerCaseName = name.toLowerCase();

          if (countryToFind) {
            //Sometimes RENAPER returns "ESTADOS_UNIDOS" instead of "ESTADOS UNIDOS"
            if (countryToFind === "ESTADOS_UNIDOS") {
              return "estados unidos" === lowerCaseName;
            }
            if (countryToFind === "TERRITORIO_CHINO_de_TAIWAN") {
              return "taiwan" === lowerCaseName;
            }
            if (countryToFind === "COSTA_RICA") {
              return "costa rica" === lowerCaseName;
            }
            return countryToFind.toLowerCase() === lowerCaseName;
          }
          return country?.toLowerCase() === lowerCaseName;
        })?.name;
      };

      const parsedCitizenship = countries.find(({ citizenship }) => {
        const lowerCaseCitizenship = citizenship?.toLowerCase();

        // Typo in Renaper API
        if (nationality === "COSTARICENSE") {
          return "costarricense" === lowerCaseCitizenship;
        }
        if (nationality === "SUDAFRICANA") {
          return "surafricana" === lowerCaseCitizenship;
        }

        return nationality?.toLowerCase() === lowerCaseCitizenship;
      })?.citizenship;

      const getAddress = () => {
        let cityParsed = city;
        let municipalityParsed = municipality;
        if (city === "SIN INFORMAR") cityParsed = "";
        if (municipality === "SIN INFORMAR") municipalityParsed = "";

        // Just for juridical
        if (addressKey === "representativesAddresses") {
          if (
            !isExternalInformation ||
            (isExternalInformation && !values[addressKey]?.street)
          ) {
            return {
              birthCountry: getParsedCountry(countryBirth),
              [addressKey]: {
                fullAddress: `${streetAddress} ${numberStreet} ${cityParsed} ${municipalityParsed}`,
                country: getParsedCountry(country),
              },
            };
          }
        } else {
          // Physical and signatory
          if (!isExternalInformation) {
            return {
              birthCountry: getParsedCountry(countryBirth),
              [addressKey]: {
                fullAddress: `${streetAddress} ${numberStreet} ${cityParsed} ${municipalityParsed}`,
                country: getParsedCountry(country),
              },
            };
          }
        }
        return {};
      };

      const body = {
        name: names,
        lastName: capitalizeString(lastNames),
        birthDate: birthdate,
        citizenship: parsedCitizenship,
        ...getAddress(),
      };

      checkIsEmailAvailable({
        dispatch,
        idNumber: values.identityDocument,
      });

      dispatch(onUpdateFormData({ ...body }));
      return { newValuesToForm: body };
    }
  } catch (error) {
    handleCatchRenaper(error);
  }
};

export const goToStep = ({
  dispatch,
  step,
  substep,
}: {
  dispatch: AppDispatch;
  step: number;
  substep?: number;
}) => {
  dispatch(onUpdateStep(step));
  dispatch(onUpdateReachedStep(step));
  dispatch(onUpdateCompletedStep(step - 1));
  substep && dispatch(onUpdateSubStep(substep));
};

export const isDev = import.meta.env.VITE_ENVIRONMENT === "dev";
